import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import { ArrowLeft, RefreshCw, Wand2, HelpCircle, Trash2, AlertCircle, Database, Cloud, FileText, Sparkles, ClipboardList, UserCheck, Download } from 'lucide-react';
import api from '../../services/api';
import LoaderProgress from '../../components/ui/loader-progress';
import { useAuth } from '../../context/AuthContext';

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
}

function formatDate(value) {
  if (!value) return '-';
  const raw = String(value);
  const trimmed = raw.trim();
  if (!trimmed) return '-';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-');
    return `${d}/${m}/${y}`;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return trimmed;
  return parsed.toLocaleDateString('it-IT');
}

function valueOrDash(value) {
  const v = String(value ?? '').trim();
  return v ? v : '-';
}

function moneyOrDash(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fileSizeOrDash(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes.toLocaleString('it-IT')} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const decimals = size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: decimals })} ${units[unitIndex]}`;
}

function jsonOrDash(value) {
  if (value == null) return '-';
  if (typeof value === 'string') {
    const v = value.trim();
    if (!v) return '-';
    if ((v.startsWith('{') && v.endsWith('}')) || (v.startsWith('[') && v.endsWith(']'))) {
      try {
        return JSON.stringify(JSON.parse(v), null, 2);
      } catch (e) {
        return v;
      }
    }
    return v;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return '-';
  }
}

function parseJsonValue(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    const v = value.trim();
    if (!v) return null;
    try {
      return JSON.parse(v);
    } catch (_) {
      return null;
    }
  }
  if (typeof value === 'object') return value;
  return null;
}

function reportChecklistChipProps(item) {
  const status = String(item?.status || item?.stato || '').trim();
  if (status === 'presente') return { color: 'success', suffix: '' };
  if (status === 'evidenza') return { color: 'info', suffix: ' (evidenza)' };
  return { color: 'warning', suffix: '' };
}

function auditEsitoChipProps(value) {
  const v = String(value || '');
  if (v === 'successo') return { label: 'successo', color: 'success' };
  if (v === 'errore') return { label: 'errore', color: 'error' };
  return { label: v || '-', color: 'default' };
}

function InfoRow({ icon: Icon, title, description, color, bgColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.12)',
        bgcolor: '#fff'
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
      >
        <Icon size={18} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 800, fontSize: 15, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.45, color: 'rgba(0,0,0,0.72)' }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AderDocumentoDetail() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const documentoId = useMemo(() => Number(id), [id]);
  const isAdmin = useMemo(() => String(user?.role_name || user?.role || '').toLowerCase() === 'admin', [user]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [mainTab, setMainTab] = useState(0);
  const [documentViewTab, setDocumentViewTab] = useState(0);
  const [dataTablesTab, setDataTablesTab] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportAiLoading, setReportAiLoading] = useState(false);
  const [reportMdDownloading, setReportMdDownloading] = useState(false);
  const [reportAi, setReportAi] = useState(null);
  const [reportActionOpen, setReportActionOpen] = useState(false);
  const [reportActionType, setReportActionType] = useState('');
  const [reportActionNote, setReportActionNote] = useState('');
  const [linksLoading, setLinksLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [linkDocumentoId, setLinkDocumentoId] = useState('');
  const [linkTipo, setLinkTipo] = useState('prova_notifica');
  const [linkNote, setLinkNote] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const reportAiPayload = useMemo(() => parseJsonValue(reportAi?.payload), [reportAi]);

  const fetchPdfPreviewUrl = useCallback(
    async (doc) => {
      const mimeType = String(doc?.mime_type || '');
      if (!mimeType.includes('pdf')) {
        setPdfPreviewUrl('');
        return;
      }
      setPdfPreviewLoading(true);
      try {
        const res = await api.get(`/ader/documenti/${documentoId}/preview-url`);
        setPdfPreviewUrl(String(res?.data?.url || ''));
      } catch (error) {
        setPdfPreviewUrl('');
      } finally {
        setPdfPreviewLoading(false);
      }
    },
    [documentoId]
  );

  const fetchDetail = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    setLoading(true);
    try {
      const res = await api.get(`/ader/documenti/${documentoId}`);
      const payload = res.data || null;
      setData(payload);
      await fetchPdfPreviewUrl(payload?.documento);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Errore caricando il dettaglio documento';
      toast.error(String(message));
    } finally {
      setLoading(false);
    }
  }, [documentoId, fetchPdfPreviewUrl]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    const mimeType = String(data?.documento?.mime_type || '');
    if (!mimeType.includes('pdf')) {
      setDocumentViewTab(1);
    }
  }, [data?.documento?.mime_type]);

  const fetchReportAi = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    setReportAiLoading(true);
    try {
      const res = await api.get(`/ader/documenti/${documentoId}/report-ai`);
      setReportAi(res?.data?.report || null);
    } catch (error) {
      setReportAi(null);
    } finally {
      setReportAiLoading(false);
    }
  }, [documentoId]);

  const fetchLinks = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    setLinksLoading(true);
    try {
      const res = await api.get(`/ader/documenti/${documentoId}/links`);
      setLinks(Array.isArray(res?.data?.links) ? res.data.links : []);
    } catch (error) {
      setLinks([]);
    } finally {
      setLinksLoading(false);
    }
  }, [documentoId]);

  const handleAddLink = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    const linkedId = Number(linkDocumentoId);
    if (!Number.isFinite(linkedId) || linkedId <= 0) {
      toast.error('Inserisci un ID documento valido');
      return;
    }
    setLinksLoading(true);
    try {
      await api.post(`/ader/documenti/${documentoId}/links`, {
        linked_documento_id: linkedId,
        tipo_link: linkTipo,
        note: linkNote
      });
      toast.success('Documento collegato');
      setLinkDocumentoId('');
      setLinkNote('');
      await fetchLinks();
    } catch (error) {
      toast.error('Errore collegando il documento');
    } finally {
      setLinksLoading(false);
    }
  }, [documentoId, fetchLinks, linkDocumentoId, linkNote, linkTipo]);

  const handleRemoveLink = useCallback(
    async (linkId) => {
      const id = Number(linkId);
      if (!Number.isFinite(id) || id <= 0) return;
      setLinksLoading(true);
      try {
        await api.delete(`/ader/documenti/${documentoId}/links/${id}`);
        toast.success('Collegamento rimosso');
        await fetchLinks();
      } catch (error) {
        toast.error('Errore rimuovendo il collegamento');
      } finally {
        setLinksLoading(false);
      }
    },
    [documentoId, fetchLinks]
  );

  const handleCreateReportAi = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    setReportAiLoading(true);
    try {
      await api.post(`/ader/documenti/${documentoId}/report-ai`, {
        tipo_report: 'cartella_prescrizione_notifica'
      });
      toast.success('Report creato');
      await fetchReportAi();
    } catch (error) {
      toast.error('Errore creando il report');
    } finally {
      setReportAiLoading(false);
    }
  }, [documentoId, fetchReportAi]);

  const handleDownloadReportMarkdown = useCallback(async () => {
    if (!reportAi?.id) return;
    setReportMdDownloading(true);
    try {
      const response = await api.get(`/ader/report-ai/${reportAi.id}/download-md`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/markdown;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const baseName = (data?.documento?.nome_file || `documento_${documentoId}`).replace(/\.[^.]+$/, '');
      link.setAttribute('download', `${baseName}_report.md`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download del report');
    } finally {
      setReportMdDownloading(false);
    }
  }, [data?.documento?.nome_file, documentoId, reportAi?.id]);

  const handleValidateReportAi = useCallback(
    async ({ action, note }) => {
      if (!reportAi?.id) return;
      setReportAiLoading(true);
      try {
        await api.post(`/ader/report-ai/${reportAi.id}/valida`, {
          action,
          note: note != null ? String(note) : ''
        });
        toast.success(action === 'valida' ? 'Report validato' : 'Report respinto');
        await fetchReportAi();
      } catch (error) {
        toast.error('Errore aggiornando lo stato del report');
      } finally {
        setReportAiLoading(false);
      }
    },
    [fetchReportAi, reportAi?.id]
  );

  useEffect(() => {
    if (dataTablesTab === 12) {
      fetchReportAi();
    }
  }, [dataTablesTab, fetchReportAi]);

  useEffect(() => {
    if (dataTablesTab === 13) {
      fetchLinks();
    }
  }, [dataTablesTab, fetchLinks]);

  const handleElabora = async () => {
    try {
      await api.post(`/ader/documenti/${documentoId}/elabora`);
      toast.success('Elaborazione accodata');
      setTimeout(fetchDetail, 1500);
    } catch (error) {
      toast.error('Errore accodando elaborazione');
    }
  };

  const handleDeleteDocumento = useCallback(async () => {
    if (!Number.isFinite(documentoId) || documentoId <= 0) return;
    if (String(deleteConfirm || '').trim().toUpperCase() !== 'ELIMINA') {
      toast.error('Scrivi ELIMINA per confermare');
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await api.delete(`/ader/documenti/${documentoId}`);
      const wasabiDeleted = res?.data?.wasabiDeleted;
      if (wasabiDeleted === false) {
        toast.warn('Documento eliminato dal DB, ma non dal file storage');
      } else {
        toast.success('Documento eliminato');
      }
      setDeleteOpen(false);
      setDeleteConfirm('');
      navigate('/ader/coda-validazione');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Errore eliminando il documento';
      toast.error(String(message));
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteConfirm, documentoId, navigate]);

  const handleMatching = async () => {
    try {
      await api.post(`/ader/documenti/${documentoId}/matching`);
      toast.success('Matching ricalcolato');
      fetchDetail();
    } catch (error) {
      toast.error('Errore ricalcolando matching');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <LoaderProgress size={60} />
        </Card>
      </Box>
    );
  }

  const documento = data?.documento || null;
  const estrazione = data?.estrazione || null;
  const azioni = Array.isArray(data?.azioni) ? data.azioni : [];
  const logAi = Array.isArray(data?.logAi) ? data.logAi : [];
  const auditLog = Array.isArray(data?.auditLog) ? data.auditLog : [];
  const righeDocumenti = Array.isArray(data?.righeDocumenti) ? data.righeDocumenti : [];
  const cartellaVoci = Array.isArray(data?.cartellaVoci) ? data.cartellaVoci : [];
  const pratiche = Array.isArray(data?.pratiche) ? data.pratiche : [];
  const scadenze = Array.isArray(data?.scadenze) ? data.scadenze : [];
  const task = Array.isArray(data?.task) ? data.task : [];
  const isPdf = String(documento?.mime_type || '').includes('pdf');
  const isCartella = String(documento?.tipo_documento || '').includes('cartella');
  const canLinkAllegati = isCartella || String(documento?.tipo_documento || '') === 'estratto_ruolo';
  const canCreateReportAi = canLinkAllegati;

  if (!documento) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6">Documento non trovato</Typography>
          <Button sx={{ mt: 2 }} startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/ader/coda-validazione')}>
            Torna alla coda
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Button startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/ader/coda-validazione')}>
              Coda validazione
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }} noWrap>
              {documento.nome_file || `Documento ${documento.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ID {documento.id} · stato {valueOrDash(documento.stato_elaborazione)} · caricato {formatDateTime(documento.data_caricamento)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshCw size={18} />} onClick={fetchDetail}>
              Aggiorna
            </Button>
            <Button variant="outlined" startIcon={<Wand2 size={18} />} onClick={handleMatching}>
              Matching
            </Button>
            <Button variant="contained" startIcon={<RefreshCw size={18} />} onClick={handleElabora}>
              Rielabora
            </Button>
            <Tooltip title="Come funziona l’elaborazione">
              <IconButton size="small" onClick={() => setHelpOpen(true)}>
                <HelpCircle size={18} />
              </IconButton>
            </Tooltip>
            {isAdmin ? (
              <Tooltip title="Elimina documento">
                <span>
                  <IconButton size="small" onClick={() => setDeleteOpen(true)} disabled={deleteLoading} sx={{ color: 'error.main' }}>
                    <Trash2 size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      </Card>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Come funziona l’elaborazione ADER</DialogTitle>
        <DialogContent dividers>
          <InfoRow
            icon={AlertCircle}
            title="In breve"
            description="Il sistema estrae prima il testo dal PDF e poi usa l’AI per classificare ed estrarre i dati. In base al tipo documento può attivare estrattori specifici.\nSe mancano dati o la confidence è bassa, il documento passa in coda per verifica."
            color="#b45309"
            bgColor="#fef3c7"
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <InfoRow
              icon={FileText}
              title="1) Estrazione base (PDF nativi)"
              description="Popola `testo_estratto`. Se il testo è insufficiente (o scansione), il documento resta da validare."
              color="#0284c7"
              bgColor="#e0f2fe"
            />
            <InfoRow
              icon={Sparkles}
              title="2) Classificazione AI"
              description="Determina il tipo documento e una confidence. Se la confidence è bassa, richiede intervento operatore."
              color="#7c3aed"
              bgColor="#ede9fe"
            />
            <InfoRow
              icon={ClipboardList}
              title="3) Estrazione per tipo"
              description="Attiva estrattori specifici quando disponibili: “estratto di ruolo” → righe tabellari; “cartella di pagamento” → voci/importi."
              color="#c2410c"
              bgColor="#ffedd5"
            />
            <InfoRow
              icon={UserCheck}
              title="4) Matching cliente + validazione"
              description="Propone un cliente; se serve conferma/correzione, resta in validazione."
              color="#16a34a"
              bgColor="#dcfce7"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Elimina documento</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
            <InfoRow
              icon={Database}
              title="Pulizia completa database"
              description="Rimuove il documento e tutti i riferimenti collegati (estrazioni, matching, righe/voci, log AI, audit, report AI, link allegati, task/scadenze)."
              color="#1d4ed8"
              bgColor="#dbeafe"
            />
            <Box sx={{ display: 'grid', gap: 1 }}>
              <InfoRow
                icon={Cloud}
                title="Eliminazione file su Wasabi"
                description="Tenta di eliminare anche il file archiviato su storage. Se per qualsiasi motivo lo storage non risponde, il DB viene comunque ripulito e la key rimane consultabile."
                color="#0f766e"
                bgColor="#ccfbf1"
              />
              <Box
                sx={{
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 2,
                  p: 1.25,
                  bgcolor: '#fafafa'
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Key Wasabi
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    fontFamily: 'monospace',
                    fontSize: 12.5,
                    color: 'rgba(0,0,0,0.78)',
                    wordBreak: 'break-all'
                  }}
                >
                  {String(documento?.percorso_file || '-')}
                </Box>
              </Box>
            </Box>
            <InfoRow
              icon={AlertCircle}
              title="Operazione irreversibile"
              description="Una volta eliminato, non è possibile ripristinare automaticamente il documento e i dati collegati."
              color="#b45309"
              bgColor="#fef3c7"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Per confermare, scrivi <strong>ELIMINA</strong>.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Conferma"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="ELIMINA"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteLoading || String(deleteConfirm || '').trim().toUpperCase() !== 'ELIMINA'}
            onClick={handleDeleteDocumento}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ p: 1, mb: 2 }}>
        <Tabs value={mainTab} onChange={(_, nextValue) => setMainTab(nextValue)} variant="scrollable" scrollButtons="auto">
          <Tab label="Documento" value={0} />
          <Tab label="Sintesi" value={1} />
          <Tab label="Dettagli" value={2} />
        </Tabs>
      </Card>

      {mainTab === 0 ? (
        <Card sx={{ p: 2 }}>
          <Tabs
            value={documentViewTab}
            onChange={(_, nextValue) => setDocumentViewTab(nextValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Anteprima" value={0} disabled={!isPdf} />
            <Tab label="Testo estratto" value={1} />
          </Tabs>
          <Divider sx={{ mb: 1.5 }} />

          {documentViewTab === 0 ? (
            pdfPreviewLoading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <LoaderProgress size={40} />
              </Box>
            ) : pdfPreviewUrl ? (
              <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <iframe title="Anteprima PDF" src={pdfPreviewUrl} style={{ width: '100%', height: 520, border: 0, display: 'block' }} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Anteprima non disponibile.
              </Typography>
            )
          ) : (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(0,0,0,0.03)',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: 12,
                maxHeight: 520,
                overflow: 'auto'
              }}
            >
              {String(documento.testo_estratto || '').trim() || '-'}
            </Box>
          )}
        </Card>
      ) : mainTab === 1 ? (
        <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Classificazione & Matching
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Tipo documento (AI)
                </Typography>
                <Typography variant="body2">{valueOrDash(documento.tipo_documento)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Confidence
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip size="small" label={documento.confidence_classificazione ?? '-'} />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Cliente suggerito
                </Typography>
                <Typography variant="body2">
                  {documento.cliente_suggerito_id ? `ID ${documento.cliente_suggerito_id}` : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Stato matching
                </Typography>
                <Typography variant="body2">{valueOrDash(documento.matching_stato_validazione)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Motivo match
                </Typography>
                <Typography variant="body2">{valueOrDash(documento.motivo_match)}</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Estratti
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {!estrazione ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna estrazione disponibile
              </Typography>
            ) : (
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Codice fiscale
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.codice_fiscale)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Partita IVA
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.partita_iva)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Nominativo
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.nominativo)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Ente creditore
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.ente_creditore)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Agente riscossione
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.agente_riscossione)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Indirizzo
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.indirizzo)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Numero cartella
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.numero_cartella)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Numero atto
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.numero_atto)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tributo
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.tributo)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Anno imposta
                  </Typography>
                  <Typography variant="body2">{valueOrDash(estrazione.anno_imposta)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Importo totale
                  </Typography>
                  <Typography variant="body2">{moneyOrDash(estrazione.importo_totale)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Data notifica
                  </Typography>
                  <Typography variant="body2">{formatDate(estrazione.data_notifica)}</Typography>
                </Grid>
              </Grid>
            )}
          </Card>
        </Grid>
        </Grid>
      ) : (
        <Card sx={{ p: 2 }}>
          <Tabs value={dataTablesTab} onChange={(_, nextValue) => setDataTablesTab(nextValue)} variant="scrollable" scrollButtons="auto">
            <Tab label="Documento" value={0} />
            <Tab label="Classificazione" value={1} />
            <Tab label="Matching" value={2} />
            <Tab label="Estrazione" value={3} />
            <Tab label={`Righe (${righeDocumenti.length})`} value={4} />
            <Tab label={`Voci (${cartellaVoci.length})`} value={5} />
            <Tab label={`Azioni (${azioni.length})`} value={6} />
            <Tab label={`Log AI (${logAi.length})`} value={7} />
            <Tab label={`Pratiche (${pratiche.length})`} value={8} />
            <Tab label={`Scadenze (${scadenze.length})`} value={9} />
            <Tab label={`Task (${task.length})`} value={10} />
            <Tab label={`Audit (${auditLog.length})`} value={11} />
            <Tab label="Report AI" value={12} />
            <Tab label={`Allegati (${links.length})`} value={13} />
          </Tabs>
          <Divider sx={{ mb: 1.5 }} />
          {dataTablesTab === 0 ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 260 }}><Typography variant="caption" color="text.secondary">percorso_file</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.percorso_file)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">mime_type</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.mime_type)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">dimensione_file</Typography></TableCell>
                    <TableCell>{fileSizeOrDash(documento.dimensione_file)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">numero_pagine</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.numero_pagine)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">origine_documento</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.origine_documento)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">metodo_estrazione</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.metodo_estrazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">qualita_ocr</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.qualita_ocr)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">retry_count</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.retry_count)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">errore_elaborazione</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.errore_elaborazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">note_operative</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.note_operative)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : dataTablesTab === 1 ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 260 }}><Typography variant="caption" color="text.secondary">tipo_documento</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.tipo_documento)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">confidence_score</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.confidence_classificazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">classificato_da</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.classificazione_classificato_da)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">validata</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.classificazione_validata)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">validata_da</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.classificazione_validata_da)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">data_validazione</Typography></TableCell>
                    <TableCell>{formatDateTime(documento.classificazione_data_validazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">classificazione_precedente</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.classificazione_precedente)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">log_ai_id</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.classificazione_log_ai_id)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">created_at</Typography></TableCell>
                    <TableCell>{formatDateTime(documento.classificazione_created_at)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : dataTablesTab === 2 ? (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 260 }}><Typography variant="caption" color="text.secondary">cliente_suggerito_id</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.cliente_suggerito_id)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">confidence_score</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.confidence_matching)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">metodo_match</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.metodo_match)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">motivo_match</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.motivo_match)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">stato_validazione</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.matching_stato_validazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">cliente_corretto_id</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.cliente_corretto_id)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">validato_da</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.matching_validato_da)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">data_validazione</Typography></TableCell>
                    <TableCell>{formatDateTime(documento.matching_data_validazione)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">log_ai_id</Typography></TableCell>
                    <TableCell>{valueOrDash(documento.matching_log_ai_id)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">created_at</Typography></TableCell>
                    <TableCell>{formatDateTime(documento.matching_created_at)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : dataTablesTab === 3 ? (
            !estrazione ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna estrazione disponibile
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: 260 }}><Typography variant="caption" color="text.secondary">codice_fiscale</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.codice_fiscale)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">partita_iva</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.partita_iva)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">nominativo</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.nominativo)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">indirizzo</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.indirizzo)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">numero_cartella</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.numero_cartella)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">numero_atto</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.numero_atto)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">ente_creditore</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.ente_creditore)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">agente_riscossione</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.agente_riscossione)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">importo_totale</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.importo_totale)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">capitale</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.capitale)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">sanzioni</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.sanzioni)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">interessi</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.interessi)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">aggio</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.aggio)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">spese_notifica</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.spese_notifica)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">data_notifica</Typography></TableCell>
                      <TableCell>{formatDate(estrazione.data_notifica)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">data_emissione</Typography></TableCell>
                      <TableCell>{formatDate(estrazione.data_emissione)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">data_scadenza</Typography></TableCell>
                      <TableCell>{formatDate(estrazione.data_scadenza)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">anno_imposta</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.anno_imposta)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">tributo</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.tributo)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">causale</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.causale)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">stato_rateizzazione</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.stato_rateizzazione)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">numero_rate</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.numero_rate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">rate_scadute</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.rate_scadute)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">importo_rate_scadute</Typography></TableCell>
                      <TableCell>{moneyOrDash(estrazione.importo_rate_scadute)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">anomalie_rilevate</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                          {jsonOrDash(estrazione.anomalie_rilevate)}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">riferimenti_cartelle_presupposte</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                          {jsonOrDash(estrazione.riferimenti_cartelle_presupposte)}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">testo_rilevante</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.testo_rilevante)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><Typography variant="caption" color="text.secondary">confidence_estrazione</Typography></TableCell>
                      <TableCell>{valueOrDash(estrazione.confidence_estrazione)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 4 ? (
            righeDocumenti.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna riga documento estratta
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>N°</TableCell>
                      <TableCell>Descrizione</TableCell>
                      <TableCell>Ente creditore</TableCell>
                      <TableCell>Data notifica</TableCell>
                      <TableCell align="right">Iniziale</TableCell>
                      <TableCell align="right">Sgravato</TableCell>
                      <TableCell align="right">Sospeso</TableCell>
                      <TableCell align="right">Da pagare</TableCell>
                      <TableCell align="center">Rateiz.</TableCell>
                      <TableCell align="center">Proc.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {righeDocumenti.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {valueOrDash(r.numero_documento)}
                        </TableCell>
                        <TableCell>{valueOrDash(r.descrizione)}</TableCell>
                        <TableCell>{valueOrDash(r.ente_creditore)}</TableCell>
                        <TableCell>{formatDate(r.data_notifica)}</TableCell>
                        <TableCell align="right">{moneyOrDash(r.importo_iniziale)}</TableCell>
                        <TableCell align="right">{moneyOrDash(r.importo_sgravato)}</TableCell>
                        <TableCell align="right">{moneyOrDash(r.importo_sospeso)}</TableCell>
                        <TableCell align="right">{moneyOrDash(r.importo_da_pagare)}</TableCell>
                        <TableCell align="center">{r.presenza_rateizzazioni === 1 ? 'SI' : r.presenza_rateizzazioni === 0 ? 'NO' : '-'}</TableCell>
                        <TableCell align="center">{r.presenza_procedure === 1 ? 'SI' : r.presenza_procedure === 0 ? 'NO' : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 5 ? (
            cartellaVoci.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna voce cartella estratta
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {cartellaVoci.filter((v) => v.sezione === 'tributi').length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Tributi
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell align="right">#</TableCell>
                            <TableCell>Anno</TableCell>
                            <TableCell>Codice</TableCell>
                            <TableCell>Descrizione</TableCell>
                            <TableCell align="right">Importo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cartellaVoci
                            .filter((v) => v.sezione === 'tributi')
                            .map((v) => (
                              <TableRow key={v.id} hover>
                                <TableCell align="right">{valueOrDash(v.riga_num)}</TableCell>
                                <TableCell>{valueOrDash(v.anno_imposta)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{valueOrDash(v.codice_tributo)}</TableCell>
                                <TableCell>{valueOrDash(v.descrizione)}</TableCell>
                                <TableCell align="right">{moneyOrDash(v.importo)}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : null}
                {cartellaVoci.filter((v) => v.sezione === 'dettaglio_importi').length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Dettaglio importi
                    </Typography>
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Voce</TableCell>
                            <TableCell align="right">Importo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cartellaVoci
                            .filter((v) => v.sezione === 'dettaglio_importi')
                            .map((v) => (
                              <TableRow key={v.id} hover>
                                <TableCell>{valueOrDash(v.voce)}</TableCell>
                                <TableCell align="right">{moneyOrDash(v.importo)}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : null}
              </Box>
            )
          ) : dataTablesTab === 6 ? (
            azioni.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna azione suggerita
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Creato</TableCell>
                      <TableCell>Azione</TableCell>
                      <TableCell>Urgenza</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell align="right">Pratica</TableCell>
                      <TableCell>Motivazione</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {azioni.map((a) => (
                      <TableRow key={a.id} hover>
                        <TableCell>{formatDateTime(a.created_at)}</TableCell>
                        <TableCell>{valueOrDash(a.azione_suggerita)}</TableCell>
                        <TableCell>{valueOrDash(a.livello_urgenza)}</TableCell>
                        <TableCell>{valueOrDash(a.stato)}</TableCell>
                        <TableCell align="right">{a.pratica_id ? `ID ${a.pratica_id}` : '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>{valueOrDash(a.motivazione_ai)}</Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 7 ? (
            logAi.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun log AI
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Operazione</TableCell>
                      <TableCell>Modello</TableCell>
                      <TableCell>Esito</TableCell>
                      <TableCell align="right">Conf.</TableCell>
                      <TableCell align="right">Durata</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                      <TableCell>Creato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logAi.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell>{l.id}</TableCell>
                        <TableCell>{valueOrDash(l.tipo_operazione)}</TableCell>
                        <TableCell>{valueOrDash(l.modello_ai)}</TableCell>
                        <TableCell>{valueOrDash(l.esito)}</TableCell>
                        <TableCell align="right">{valueOrDash(l.confidence_score)}</TableCell>
                        <TableCell align="right">{l.durata_ms != null ? `${l.durata_ms} ms` : '-'}</TableCell>
                        <TableCell align="right">{l.tokens_input != null || l.tokens_output != null ? `${l.tokens_input ?? 0}/${l.tokens_output ?? 0}` : '-'}</TableCell>
                        <TableCell>{formatDateTime(l.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 8 ? (
            pratiche.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna pratica collegata
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pratica</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Procedura</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell align="right">Importo</TableCell>
                      <TableCell>Creato</TableCell>
                      <TableCell>Agg.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pratiche.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.pratica_id ? `ID ${p.pratica_id}` : '-'}</TableCell>
                        <TableCell>{p.cliente_id ? `ID ${p.cliente_id}` : '-'}</TableCell>
                        <TableCell>{valueOrDash(p.tipo_procedura)}</TableCell>
                        <TableCell>{valueOrDash(p.stato)}</TableCell>
                        <TableCell align="right">{moneyOrDash(p.importo_contestato)}</TableCell>
                        <TableCell>{formatDateTime(p.created_at)}</TableCell>
                        <TableCell>{formatDateTime(p.updated_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 9 ? (
            scadenze.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna scadenza
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pratica</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Scadenza</TableCell>
                      <TableCell align="right">Alert</TableCell>
                      <TableCell>Completata</TableCell>
                      <TableCell>Creata</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scadenze.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.pratica_id ? `ID ${s.pratica_id}` : '-'}</TableCell>
                        <TableCell>{valueOrDash(s.tipo_scadenza)}</TableCell>
                        <TableCell>{formatDate(s.data_scadenza)}</TableCell>
                        <TableCell align="right">{valueOrDash(s.giorni_alert)}</TableCell>
                        <TableCell>{s.completata === 1 ? 'SI' : 'NO'}</TableCell>
                        <TableCell>{valueOrDash(s.creata_da)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 10 ? (
            task.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun task
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Titolo</TableCell>
                      <TableCell>Priorità</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Scadenza</TableCell>
                      <TableCell align="right">Pratica</TableCell>
                      <TableCell align="right">Assegnato</TableCell>
                      <TableCell>Creato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {task.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{valueOrDash(t.titolo)}</TableCell>
                        <TableCell>{valueOrDash(t.priorita)}</TableCell>
                        <TableCell>{valueOrDash(t.stato)}</TableCell>
                        <TableCell>{formatDate(t.scadenza_task)}</TableCell>
                        <TableCell align="right">{t.pratica_id ? `ID ${t.pratica_id}` : '-'}</TableCell>
                        <TableCell align="right">{t.assegnato_a ? `ID ${t.assegnato_a}` : '-'}</TableCell>
                        <TableCell>{formatDateTime(t.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 11 ? (
            auditLog.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun evento audit
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Creato</TableCell>
                      <TableCell>Utente</TableCell>
                      <TableCell>Azione</TableCell>
                      <TableCell>Esito</TableCell>
                      <TableCell>Dettaglio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditLog.map((r) => {
                      const chip = auditEsitoChipProps(r.esito);
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>{formatDateTime(r.created_at)}</TableCell>
                          <TableCell>{r.user_id ? `ID ${r.user_id}` : '-'}</TableCell>
                          <TableCell>{valueOrDash(r.azione)}</TableCell>
                          <TableCell><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
                          <TableCell>
                            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                              {r.errore_dettaglio ? String(r.errore_dettaglio) : jsonOrDash(r.payload)}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : dataTablesTab === 12 ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Report AI
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prescrizione e regolarità notifica (AI-assistito)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={fetchReportAi} disabled={reportAiLoading}>
                    Aggiorna
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download size={16} />}
                    onClick={handleDownloadReportMarkdown}
                    disabled={reportAiLoading || reportMdDownloading || !reportAi?.id}
                  >
                    Scarica MD
                  </Button>
                  {reportAi?.id ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={reportAiLoading || reportAi?.stato === 'validato'}
                        onClick={() => {
                          setReportActionType('valida');
                          setReportActionNote('');
                          setReportActionOpen(true);
                        }}
                      >
                        Valida
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={reportAiLoading || reportAi?.stato === 'respinto'}
                        onClick={() => {
                          setReportActionType('respingi');
                          setReportActionNote('');
                          setReportActionOpen(true);
                        }}
                      >
                        Respingi
                      </Button>
                    </>
                  ) : null}
                  <Button size="small" variant="contained" onClick={handleCreateReportAi} disabled={reportAiLoading || !canCreateReportAi}>
                    Crea bozza
                  </Button>
                </Box>
              </Box>

              <Dialog open={reportActionOpen} onClose={() => setReportActionOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{reportActionType === 'respingi' ? 'Respingi report' : 'Valida report'}</DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Questa operazione aggiorna lo stato del report e viene tracciata in audit.
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    label="Note (opzionale)"
                    value={reportActionNote}
                    onChange={(e) => setReportActionNote(e.target.value)}
                    placeholder="Inserisci eventuali note per la validazione/respinta…"
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setReportActionOpen(false)} disabled={reportAiLoading}>
                    Annulla
                  </Button>
                  <Button
                    variant="contained"
                    color={reportActionType === 'respingi' ? 'error' : 'success'}
                    disabled={reportAiLoading || !reportAi?.id}
                    onClick={async () => {
                      const action = reportActionType === 'respingi' ? 'respingi' : 'valida';
                      await handleValidateReportAi({ action, note: reportActionNote });
                      setReportActionOpen(false);
                    }}
                  >
                    Conferma
                  </Button>
                </DialogActions>
              </Dialog>

              {!canCreateReportAi ? (
                <Typography variant="body2" color="text.secondary">
                  Il report è previsto per le cartelle di pagamento e per gli estratti di ruolo.
                </Typography>
              ) : reportAiLoading ? (
                <Box sx={{ py: 2 }}>
                  <LoaderProgress size={40} />
                </Box>
              ) : !reportAi ? (
                <Typography variant="body2" color="text.secondary">
                  Nessun report presente. Crea una bozza per iniziare.
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Stato
                    </Typography>
                    <Typography variant="body2">{valueOrDash(reportAi.stato)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Tipo
                    </Typography>
                    <Typography variant="body2">{valueOrDash(reportAi.tipo_report)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Creato
                    </Typography>
                    <Typography variant="body2">{formatDateTime(reportAi.creato_il)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Prescrizione
                    </Typography>
                    <Typography variant="body2">{valueOrDash(reportAi.esito_prescrizione)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Notifica
                    </Typography>
                    <Typography variant="body2">{valueOrDash(reportAi.esito_notifica)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      Confidence
                    </Typography>
                    <Typography variant="body2">
                      {reportAi.confidence != null ? `${Number(reportAi.confidence)}%` : '-'}
                    </Typography>
                  </Grid>
                  {reportAiPayload?.report_testo ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Report (testo)
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'grid', gap: 1 }}>
                        {reportAiPayload.report_testo.sintesi ? (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {String(reportAiPayload.report_testo.sintesi)}
                          </Typography>
                        ) : null}

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Prescrizione
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {valueOrDash(reportAiPayload.report_testo?.prescrizione?.esito)}
                            </Typography>
                            {reportAiPayload.report_testo?.prescrizione?.motivazione ? (
                              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                {String(reportAiPayload.report_testo.prescrizione.motivazione)}
                              </Typography>
                            ) : null}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Notifica
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {valueOrDash(reportAiPayload.report_testo?.notifica?.esito)}
                            </Typography>
                            {reportAiPayload.report_testo?.notifica?.motivazione ? (
                              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                {String(reportAiPayload.report_testo.notifica.motivazione)}
                              </Typography>
                            ) : null}
                          </Grid>
                        </Grid>

                        {Array.isArray(reportAiPayload.report_testo?.cosa_manca) && reportAiPayload.report_testo.cosa_manca.length ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Cosa manca
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'grid', gap: 0.25 }}>
                              {reportAiPayload.report_testo.cosa_manca.slice(0, 12).map((x, idx) => (
                                <Typography key={`manca-${idx}`} variant="body2" color="text.secondary">
                                  - {String(x)}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                      </Box>
                    </Grid>
                  ) : reportAiPayload?.report_testo_error ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Report (testo)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {String(reportAiPayload.report_testo_error)}
                      </Typography>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Blocchi / Checklist
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1, mt: 0.5 }}>
                      {Array.isArray(reportAiPayload?.gatekeeping?.blockers) && reportAiPayload.gatekeeping.blockers.length ? (
                        <>
                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {reportAiPayload.gatekeeping.blockers.map((b) => (
                              <Chip key={String(b.key || b.label)} size="small" color="error" label={String(b.label || b.key)} />
                            ))}
                          </Box>
                          <Box sx={{ display: 'grid', gap: 0.5 }}>
                            {reportAiPayload.gatekeeping.blockers.map((b) => (
                              <Typography key={`reason-${String(b.key || b.label)}`} variant="body2" color="text.secondary">
                                - {String(b.reason || '')}
                              </Typography>
                            ))}
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Nessun blocco rilevato
                        </Typography>
                      )}

                      {Array.isArray(reportAiPayload?.checklist) && reportAiPayload.checklist.length ? (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {reportAiPayload.checklist.map((c) => {
                            const p = reportChecklistChipProps(c);
                            const labelBase = `${String(c.label || c.key)}${c.required ? ' *' : ''}${p.suffix}`;
                            return (
                              <Tooltip
                                key={String(c.key || c.label)}
                                title={c.source ? `Fonte: ${String(c.source)}` : 'Fonte: n/d'}
                              >
                                <Chip size="small" color={p.color} label={labelBase} />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ) : null}
                    </Box>
                  </Grid>
                  {Array.isArray(reportAiPayload?.timeline) && reportAiPayload.timeline.length ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Timeline
                      </Typography>
                      <TableContainer sx={{ mt: 0.5, maxHeight: 260 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Evento</TableCell>
                              <TableCell>Data</TableCell>
                              <TableCell>Fonte</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportAiPayload.timeline.map((t, idx) => (
                              <TableRow key={`${String(t.tipo || 'ev')}-${idx}`} hover>
                                <TableCell>{valueOrDash(t.tipo)}</TableCell>
                                <TableCell>{valueOrDash(t.label || t.tipo)}</TableCell>
                                <TableCell>{valueOrDash(t.data)}</TableCell>
                                <TableCell>{valueOrDash(t.fonte)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  ) : null}
                  {Array.isArray(reportAiPayload?.evidenze) && reportAiPayload.evidenze.length ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Evidenze (testo)
                      </Typography>
                      <TableContainer sx={{ mt: 0.5, maxHeight: 260 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Documento</TableCell>
                              <TableCell>Data</TableCell>
                              <TableCell>Snippet</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportAiPayload.evidenze.slice(0, 15).map((e, idx) => (
                              <TableRow key={`${String(e.key || 'ev')}-${idx}`} hover>
                                <TableCell>{valueOrDash(e.label || e.key)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {e.documento_nome ? String(e.documento_nome) : e.documento_id ? `ID ${e.documento_id}` : '-'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {e.tipo_link ? String(e.tipo_link) : 'principale'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{valueOrDash(e.data)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {valueOrDash(e.snippet)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  ) : null}
                  {Array.isArray(reportAiPayload?.eventi) && reportAiPayload.eventi.length ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Eventi (testo)
                      </Typography>
                      <TableContainer sx={{ mt: 0.5, maxHeight: 260 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Documento</TableCell>
                              <TableCell>Data</TableCell>
                              <TableCell>Snippet</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportAiPayload.eventi.slice(0, 15).map((e, idx) => (
                              <TableRow key={`${String(e.tipo || 'ev')}-${idx}`} hover>
                                <TableCell>{valueOrDash(e.label || e.tipo)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {e.documento_nome ? String(e.documento_nome) : e.documento_id ? `ID ${e.documento_id}` : '-'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {e.tipo_link ? String(e.tipo_link) : 'principale'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{valueOrDash(e.data)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {valueOrDash(e.snippet)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Payload
                    </Typography>
                    <Box sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 }}>
                      {jsonOrDash(reportAi.payload)}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          ) : dataTablesTab === 13 ? (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Allegati
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Collega prove notifica e atti interruttivi al documento
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={fetchLinks} disabled={linksLoading}>
                  Aggiorna
                </Button>
              </Box>

              {!canLinkAllegati ? (
                <Typography variant="body2" color="text.secondary">
                  Gli allegati sono disponibili per le cartelle di pagamento e per gli estratti di ruolo.
                </Typography>
              ) : (
                <>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        label="ID documento"
                        value={linkDocumentoId}
                        onChange={(e) => setLinkDocumentoId(e.target.value)}
                        placeholder="Es. 15"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        size="small"
                        fullWidth
                        select
                        label="Tipo collegamento"
                        value={linkTipo}
                        onChange={(e) => setLinkTipo(e.target.value)}
                      >
                        <MenuItem value="prova_notifica">Prova notifica</MenuItem>
                        <MenuItem value="atto_interruttivo">Atto interruttivo</MenuItem>
                        <MenuItem value="altro">Altro</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Note (opzionale)"
                        value={linkNote}
                        onChange={(e) => setLinkNote(e.target.value)}
                        placeholder="Es. Relata / ricevuta PEC / intimazione…"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        size="small"
                        variant="contained"
                        onClick={handleAddLink}
                        disabled={linksLoading}
                      >
                        Collega
                      </Button>
                    </Grid>
                  </Grid>

                  {linksLoading ? (
                    <Box sx={{ py: 2 }}>
                      <LoaderProgress size={40} />
                    </Box>
                  ) : links.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nessun allegato collegato
                    </Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 420 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Documento</TableCell>
                            <TableCell>Caricato</TableCell>
                            <TableCell align="right">Azioni</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {links.map((l) => (
                            <TableRow key={l.id} hover>
                              <TableCell>{valueOrDash(l.tipo_link)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {l.nome_file || `ID ${l.documento_figlio_id}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID {l.documento_figlio_id} · {valueOrDash(l.tipo_documento)}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDateTime(l.data_caricamento)}</TableCell>
                              <TableCell align="right">
                                <Tooltip title="Rimuovi collegamento">
                                  <IconButton size="small" onClick={() => handleRemoveLink(l.id)}>
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          ) : !estrazione ? (
            <Typography variant="body2" color="text.secondary">
              Nessuna estrazione disponibile
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ width: 260 }}><Typography variant="caption" color="text.secondary">codice_fiscale</Typography></TableCell>
                    <TableCell>{valueOrDash(estrazione.codice_fiscale)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}
    </Box>
  );
}
